/**
 * charts.js - Chart.js wrapper for XY Plot visualizations
 *
 * Provides:
 * - XY line charts for CFD data
 * - Chart export to PNG
 * - Data export to CSV
 * - Interactive chart updates
 */

class ChartManager {
  constructor() {
    this.charts = {};
    this.chartData = {};
  }

  /**
   * Map a value to a color based on colormap
   * Uses the global Colormap module for consistency with mesh viewer
   * @param {number} value - Value to map
   * @param {number} min - Minimum value
   * @param {number} max - Maximum value
   * @param {string} colormap - Colormap name
   * @returns {string} RGB color string
   */
  valueToColor(value, min, max, colormap = 'jet') {
    // Check if Colormap module is available
    if (!window.Colormap) {
      console.warn('Colormap module not available, using default color');
      return 'rgb(75, 192, 192)';
    }

    // Normalize value to [0, 1] range
    const range = max - min;
    const normalized = range === 0 ? 0 : (value - min) / range;

    // Get solid color for main mesh colormap dropdown if using solid colormap
    const solidColorHex = window.Colormap.isSolidColor(colormap)
      ? window.Colormap.getSolidColor('colormapPreset')
      : null;

    // Use the Colormap module to get RGB values (ensures consistency with mesh viewer)
    const rgb = window.Colormap.getColor(normalized, colormap, solidColorHex);

    return `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
  }

  /**
   * Create or update XY line chart
   * @param {string} canvasId - Canvas element ID
   * @param {Array<number>} xData - X-axis data (distances or coordinates)
   * @param {Array<number>} yData - Y-axis data (field values)
   * @param {Object} options - Chart options
   * @returns {Chart} Chart.js instance
   */
  createXYChart(canvasId, xData, yData, options = {}) {
    const canvas = document.getElementById(canvasId);
    if (!canvas) {
      console.error(`Canvas ${canvasId} not found`);
      return null;
    }

    // Get 2D context
    const ctx = canvas.getContext('2d');

    // Destroy existing chart if present
    if (this.charts[canvasId]) {
      this.charts[canvasId].destroy();
    }

    // Default options
    const defaultOptions = {
      xLabel: 'Distance (m)',
      yLabel: 'Value',
      title: 'XY Plot',
      lineColor: 'rgb(75, 192, 192)',
      backgroundColor: 'rgba(75, 192, 192, 0.2)',
      showPoints: true,
      tension: 0.1,
      colormap: null,  // Colormap name (jet, viridis, plasma, coolwarm, rainbow)
      colormapMin: null,  // Min value for colormap (null = auto from data)
      colormapMax: null   // Max value for colormap (null = auto from data)
    };

    const chartOptions = { ...defaultOptions, ...options };

    // Store data for export
    this.chartData[canvasId] = {
      x: xData,
      y: yData,
      options: chartOptions
    };

    // Prepare data as {x, y} points for proper linear plotting
    const chartData = xData.map((x, i) => ({
      x: x,
      y: yData[i]
    }));

    // Determine colormap range from valid (non-null) data if colormap is enabled
    let useColormap = false;
    let colormapMin, colormapMax;

    if (chartOptions.colormap) {
      // Filter out null values for range calculation
      const validYValues = yData.filter(y => y !== null && y !== undefined);

      if (validYValues.length > 0) {
        useColormap = true;
        colormapMin = chartOptions.colormapMin !== null ? chartOptions.colormapMin : Math.min(...validYValues);
        colormapMax = chartOptions.colormapMax !== null ? chartOptions.colormapMax : Math.max(...validYValues);

        console.log(`Using colormap '${chartOptions.colormap}' with range [${colormapMin}, ${colormapMax}]`);
      }
    }

    // Generate point colors if colormap is enabled
    let pointColors = [];
    if (useColormap) {
      pointColors = yData.map(y => {
        if (y === null || y === undefined) {
          return 'rgba(128, 128, 128, 0.3)';  // Gray for null values
        }
        return this.valueToColor(y, colormapMin, colormapMax, chartOptions.colormap);
      });
    }

    // Capture context for plugin callbacks
    const self = this;

    // Create Chart.js chart
    const chart = new Chart(ctx, {
      type: 'scatter',
      data: {
        datasets: [{
          label: chartOptions.yLabel,
          data: chartData,
          borderColor: useColormap ? 'transparent' : chartOptions.lineColor,
          backgroundColor: useColormap ? 'transparent' : chartOptions.backgroundColor,
          borderWidth: 2,
          pointRadius: chartOptions.showPoints ? (useColormap ? 4 : 3) : 0,
          pointHoverRadius: 6,
          pointBackgroundColor: useColormap ? pointColors : chartOptions.backgroundColor,
          pointBorderColor: useColormap ? pointColors : chartOptions.lineColor,
          pointBorderWidth: useColormap ? 1 : 0,
          showLine: true,
          spanGaps: false,  // Don't connect points across null values - create gaps
          tension: chartOptions.tension,
          fill: false,
          segment: useColormap ? {
            // Dynamic segment coloring based on the value at the start of each segment
            borderColor: (ctx) => {
              const p0 = ctx.p0;
              const p1 = ctx.p1;

              // If either point is null/undefined, use gray
              if (!p0.parsed || !p1.parsed ||
                  p0.parsed.y === null || p0.parsed.y === undefined ||
                  p1.parsed.y === null || p1.parsed.y === undefined) {
                return 'rgba(128, 128, 128, 0.3)';
              }

              // Use average of the two points for segment color
              const avgValue = (p0.parsed.y + p1.parsed.y) / 2;
              return self.valueToColor(avgValue, colormapMin, colormapMax, chartOptions.colormap);
            },
            borderWidth: 3
          } : undefined
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 2,
        layout: {
          padding: {
            right: useColormap ? 100 : 20  // Extra padding for colormap legend
          }
        },
        plugins: {
          title: {
            display: true,
            text: chartOptions.title,
            font: {
              size: 14,
              weight: 'bold'
            },
            color: '#ffffff'
          },
          legend: {
            display: !useColormap,  // Hide default legend when using colormap
            position: 'top',
            labels: {
              color: '#ffffff'
            }
          },
          tooltip: {
            mode: 'nearest',
            intersect: false,
            callbacks: {
              label: function(context) {
                const x = context.parsed.x;
                const y = context.parsed.y;
                return `${chartOptions.xLabel}: ${x.toFixed(4)}, ${chartOptions.yLabel}: ${y.toFixed(3)}`;
              }
            }
          }
        },
        scales: {
          x: {
            type: 'linear',
            position: 'bottom',
            title: {
              display: true,
              text: chartOptions.xLabel,
              font: {
                size: 12,
                weight: 'bold'
              },
              color: '#ffffff'
            },
            ticks: {
              maxTicksLimit: 10,
              callback: function(value, index, ticks) {
                return value.toFixed(3);
              },
              color: '#cccccc'
            },
            grid: {
              color: '#444444'
            }
          },
          y: {
            title: {
              display: true,
              text: chartOptions.yLabel,
              font: {
                size: 12,
                weight: 'bold'
              },
              color: '#ffffff'
            },
            ticks: {
              callback: function(value, index, ticks) {
                // Use fixed notation for small ranges, exponential for large
                const absValue = Math.abs(value);
                if (absValue > 1000 || (absValue < 0.01 && absValue > 0)) {
                  return value.toExponential(2);
                }
                return value.toFixed(2);
              },
              color: '#cccccc'
            },
            grid: {
              color: '#444444'
            }
          }
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false
        }
      },
      plugins: useColormap ? [
        // Black background plugin
        {
          id: 'blackBackground',
          beforeDraw: (chart) => {
            const ctx = chart.ctx;
            ctx.save();
            ctx.fillStyle = '#000000';
            ctx.fillRect(0, 0, chart.width, chart.height);
            ctx.restore();
          }
        },
        // Colormap legend plugin
        {
          id: 'colormapLegend',
          afterDraw: (chart) => {
            const ctx = chart.ctx;
            const chartArea = chart.chartArea;

            // Legend dimensions
            const legendWidth = 20;
            const legendHeight = chartArea.height * 0.8;
            const legendX = chartArea.right + 30;
            const legendY = chartArea.top + (chartArea.height - legendHeight) / 2;

            // Draw gradient
            const gradient = ctx.createLinearGradient(0, legendY + legendHeight, 0, legendY);

            // Sample colors from colormap
            const steps = 20;
            for (let i = 0; i <= steps; i++) {
              const t = i / steps;
              const value = colormapMin + (colormapMax - colormapMin) * t;
              const color = self.valueToColor(value, colormapMin, colormapMax, chartOptions.colormap);
              gradient.addColorStop(t, color);
            }

            ctx.fillStyle = gradient;
            ctx.fillRect(legendX, legendY, legendWidth, legendHeight);

            // Draw border
            ctx.strokeStyle = '#ffffff';
            ctx.lineWidth = 1;
            ctx.strokeRect(legendX, legendY, legendWidth, legendHeight);

            // Draw labels
            ctx.fillStyle = '#ffffff';
            ctx.font = '11px Arial';
            ctx.textAlign = 'left';

            const numLabels = 5;
            for (let i = 0; i <= numLabels; i++) {
              const t = i / numLabels;
              const value = colormapMin + (colormapMax - colormapMin) * (1 - t);
              const y = legendY + legendHeight * t;

              // Format value
              let label;
              const absValue = Math.abs(value);
              if (absValue > 1000 || (absValue < 0.01 && absValue > 0)) {
                label = value.toExponential(2);
              } else {
                label = value.toFixed(2);
              }

              // Draw tick mark
              ctx.beginPath();
              ctx.moveTo(legendX + legendWidth, y);
              ctx.lineTo(legendX + legendWidth + 5, y);
              ctx.strokeStyle = '#ffffff';
              ctx.stroke();

              // Draw label
              ctx.fillText(label, legendX + legendWidth + 8, y + 4);
            }
          }
        }
      ] : [
        // Black background plugin (always apply)
        {
          id: 'blackBackground',
          beforeDraw: (chart) => {
            const ctx = chart.ctx;
            ctx.save();
            ctx.fillStyle = '#000000';
            ctx.fillRect(0, 0, chart.width, chart.height);
            ctx.restore();
          }
        }
      ]
    });

    this.charts[canvasId] = chart;
    return chart;
  }

  /**
   * Update existing chart with new data
   * @param {string} canvasId - Canvas element ID
   * @param {Array<number>} xData - New X-axis data
   * @param {Array<number>} yData - New Y-axis data
   */
  updateChart(canvasId, xData, yData) {
    const chart = this.charts[canvasId];
    if (!chart) {
      console.warn(`No chart found for ${canvasId}`);
      return;
    }

    // Prepare data as {x, y} points
    const chartData = xData.map((x, i) => ({
      x: x,
      y: yData[i]
    }));

    // Update data
    chart.data.datasets[0].data = chartData;

    // Update stored data
    this.chartData[canvasId].x = xData;
    this.chartData[canvasId].y = yData;

    // Re-render
    chart.update();
  }

  /**
   * Export chart to PNG
   * @param {string} canvasId - Canvas element ID
   * @param {string} filename - Output filename
   */
  exportChartToPNG(canvasId, filename = 'chart.png') {
    const chart = this.charts[canvasId];
    if (!chart) {
      console.error(`No chart found for ${canvasId}`);
      return;
    }

    // Get canvas as data URL
    const url = chart.toBase64Image();

    // Create download link
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();

    console.log(`Exported chart to ${filename}`);
  }

  /**
   * Export chart data to CSV
   * @param {string} canvasId - Canvas element ID
   * @param {string} filename - Output filename
   */
  exportChartToCSV(canvasId, filename = 'data.csv') {
    const data = this.chartData[canvasId];
    if (!data) {
      console.error(`No data found for ${canvasId}`);
      return;
    }

    const { x, y, options } = data;

    // Create CSV content (skip null values)
    let csv = `${options.xLabel},${options.yLabel}\n`;
    for (let i = 0; i < x.length; i++) {
      // Skip null values (points outside mesh)
      if (y[i] !== null && y[i] !== undefined) {
        csv += `${x[i]},${y[i]}\n`;
      }
    }

    // Create Blob and download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);

    console.log(`Exported data to ${filename}`);
  }

  /**
   * Export chart to SVG
   * @param {string} canvasId - Canvas element ID
   * @param {string} filename - Output filename
   */
  exportChartToSVG(canvasId, filename = 'chart.svg') {
    const chart = this.charts[canvasId];
    if (!chart) {
      console.error(`No chart found for ${canvasId}`);
      return;
    }

    // Get canvas element
    const canvas = chart.canvas;

    // Create SVG with embedded image
    const imgData = canvas.toDataURL('image/png');
    const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
     width="${canvas.width}" height="${canvas.height}" viewBox="0 0 ${canvas.width} ${canvas.height}">
  <image width="${canvas.width}" height="${canvas.height}" xlink:href="${imgData}"/>
</svg>`;

    // Create Blob and download
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);

    console.log(`Exported chart to ${filename}`);
  }

  /**
   * Export chart to PDF
   * @param {string} canvasId - Canvas element ID
   * @param {string} filename - Output filename
   */
  exportChartToPDF(canvasId, filename = 'chart.pdf') {
    const chart = this.charts[canvasId];
    if (!chart) {
      console.error(`No chart found for ${canvasId}`);
      return;
    }

    // Check if jsPDF is available
    if (!window.jspdf || !window.jspdf.jsPDF) {
      console.error('jsPDF library not found');
      alert('PDF export requires jsPDF library');
      return;
    }

    // Get canvas element and image data
    const canvas = chart.canvas;
    const imgData = canvas.toDataURL('image/png');

    // Calculate PDF dimensions (convert px to mm, assuming 96 DPI)
    const pxToMm = 0.264583;
    const pdfWidth = canvas.width * pxToMm;
    const pdfHeight = canvas.height * pxToMm;

    // Create PDF with appropriate size
    const { jsPDF } = window.jspdf;
    const orientation = pdfWidth > pdfHeight ? 'landscape' : 'portrait';
    const pdf = new jsPDF({
      orientation: orientation,
      unit: 'mm',
      format: [pdfWidth, pdfHeight]
    });

    // Add image to PDF
    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);

    // Save PDF
    pdf.save(filename);

    console.log(`Exported chart to ${filename}`);
  }

  /**
   * Destroy chart instance
   * @param {string} canvasId - Canvas element ID
   */
  destroyChart(canvasId) {
    if (this.charts[canvasId]) {
      this.charts[canvasId].destroy();
      delete this.charts[canvasId];
      delete this.chartData[canvasId];
    }
  }

  /**
   * Destroy all charts
   */
  destroyAll() {
    Object.keys(this.charts).forEach(canvasId => {
      this.destroyChart(canvasId);
    });
  }

  /**
   * Get chart instance
   * @param {string} canvasId - Canvas element ID
   * @returns {Chart|null} Chart.js instance or null
   */
  getChart(canvasId) {
    return this.charts[canvasId] || null;
  }

  /**
   * Create multi-series XY chart
   * @param {string} canvasId - Canvas element ID
   * @param {Array<Object>} series - Array of {x, y, label} objects
   * @param {Object} options - Chart options
   */
  createMultiSeriesChart(canvasId, series, options = {}) {
    const canvas = document.getElementById(canvasId);
    if (!canvas) {
      console.error(`Canvas ${canvasId} not found`);
      return null;
    }

    const ctx = canvas.getContext('2d');

    // Destroy existing chart
    if (this.charts[canvasId]) {
      this.charts[canvasId].destroy();
    }

    // Color palette
    const colors = [
      'rgb(75, 192, 192)',
      'rgb(255, 99, 132)',
      'rgb(54, 162, 235)',
      'rgb(255, 206, 86)',
      'rgb(153, 102, 255)',
      'rgb(255, 159, 64)'
    ];

    // Default options
    const defaultOptions = {
      xLabel: 'Distance (m)',
      yLabel: 'Value',
      title: 'XY Plot',
      showPoints: true,
      tension: 0.1
    };

    const chartOptions = { ...defaultOptions, ...options };

    // Create datasets
    const datasets = series.map((s, i) => ({
      label: s.label || `Series ${i + 1}`,
      data: s.y,
      borderColor: colors[i % colors.length],
      backgroundColor: colors[i % colors.length].replace('rgb', 'rgba').replace(')', ', 0.2)'),
      borderWidth: 2,
      pointRadius: chartOptions.showPoints ? 2 : 0,
      pointHoverRadius: 5,
      tension: chartOptions.tension,
      fill: false
    }));

    // Use first series for X labels
    const xLabels = series[0].x.map(x => x.toFixed(4));

    // Create chart
    const chart = new Chart(ctx, {
      type: 'line',
      data: {
        labels: xLabels,
        datasets: datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 2,
        plugins: {
          title: {
            display: true,
            text: chartOptions.title,
            font: {
              size: 14,
              weight: 'bold'
            }
          },
          legend: {
            display: true,
            position: 'top'
          }
        },
        scales: {
          x: {
            type: 'linear',
            position: 'bottom',
            title: {
              display: true,
              text: chartOptions.xLabel
            }
          },
          y: {
            title: {
              display: true,
              text: chartOptions.yLabel
            }
          }
        }
      }
    });

    this.charts[canvasId] = chart;
    this.chartData[canvasId] = { series, options: chartOptions };

    return chart;
  }
}

// Global chart manager instance
window.chartManager = new ChartManager();

console.log('ChartManager initialized');
